/* eslint-disable import/no-cycle */
import request from 'utils/request'
import { LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS } from 'utils/constants'
import { loadingActions } from 'store/loader'
import { navigateTo } from 'navigation'
import packageJson from '../../package.json'

const SET_CAREGIVER_PARTICIPANTS = 'SET_CAREGIVER_PARTICIPANTS'
const SET_SELECTED_PTP = 'SET_SELECTED_PTP'
const SET_SWITCHING_PTP = 'SET_SWITCHING_PTP'
const USER_LOGGED_OUT = 'USER_LOGGED_OUT'
const SET_USER_DATA = 'SET_USER_DATA'

export const caregiverActions = {
    fetchParticipants: () => (dispatch, getState) => {
        const { user } = getState()
        const {
            is_caregiver: isCaregiver,
            is_temporary_password: isTempPassword
        } = user || {}
        // If the user is not a caregiver or is still using a temporary
        // password (first-login flow), do not fetch participants yet.
        // For caregivers, fetching participants can trigger an automatic
        // participant selection followed by navigation and a full page
        // reload; running this while they are on the change-password
        // screen can disrupt the password inputs.
        if (!isCaregiver || isTempPassword) return Promise.resolve(true)
        const url = `/control/caregiver/me/participants`
        const success = json => {
            const map = json?.caregiver_participants || {}
            const participants = Object.values(map)
            // Store participants in sessionStorage
            try {
                sessionStorage.setItem(SESSION_STORAGE_KEYS.caregiverParticipants, JSON.stringify(participants))
            } catch (_) { }
            dispatch({ type: SET_CAREGIVER_PARTICIPANTS, participants })

            // Helper: navigate to Select Participant if we're not already there
            const goToSelectParticipant = () => {
                try {
                    const { location } = getState()
                    const currentPath = location?.pathname || window.location?.pathname || ''
                    if (!currentPath.includes('/select-ptp')) {
                        navigateTo('/select-ptp')
                    }
                } catch (_) { }
            }

            // If there's exactly 1 participant, auto-select it and call token API
            // If there are multiple participants, wait for user to manually select
            if (participants.length === 1) {
                const only = participants[0]
                // Auto-select the only participant - setSelected will:
                // 1. Call the token API: POST /control/caregiver/me/participant/{id}/token
                // 2. Update the user's authentication token
                // 3. Reload the page with the new token
                dispatch(caregiverActions.setSelected(only))
            } else if (participants.length > 1) {
                // Preserve any existing selection across refreshes/navigation
                // Only clear if there is truly no prior selection
                let priorSelected = null
                try {
                    priorSelected = JSON.parse(localStorage.getItem('selected_ptp')) || null
                } catch (_) { priorSelected = null }
                const { caregiver } = getState()
                const currentSelected = caregiver?.selected || priorSelected
                if (!currentSelected) {
                    dispatch({ type: SET_SELECTED_PTP, ptp: null })
                    // First-time caregiver with multiple participants: send to Select Participant
                    goToSelectParticipant()
                }
            } else if (participants.length === 0) {
                // No assignment yet: clear any selection and route to selector
                // page that shows "assignment required".
                dispatch({ type: SET_SELECTED_PTP, ptp: null })
                goToSelectParticipant()
            }
        }
        return dispatch(
            request({ method: 'GET', url, success, hasLoader: true })
        )
    },
    setSelected: ptp => (dispatch, getState) => {
        dispatch({ type: SET_SWITCHING_PTP, switching: true })
        // Optimistically set selection so UI updates immediately
        dispatch({ type: SET_SELECTED_PTP, ptp })
        const participantId = ptp?.id
        if (!participantId) return Promise.resolve(true)
        // Call token API to get participant-specific authentication token
        const url = `/control/caregiver/me/participant/${participantId}/token`
        const success = json => {
            try {
                const decoded = JSON.parse(atob(json.token.split('.')[1]))
                // Normalize exp to seconds (handles ms numbers and string values)
                let exp = decoded?.exp
                if (typeof exp === 'string') exp = Number(exp)
                if (typeof exp === 'number' && exp > 1e12) exp = Math.floor(exp / 1000)
                const normalized = exp !== undefined && exp !== null ? { ...decoded, exp } : decoded

                const { user } = getState()
                const payload = { ...user, ...normalized, token: json.token }
                localStorage.setItem(LOCAL_STORAGE_KEYS.user, JSON.stringify(payload))
                dispatch({ type: SET_USER_DATA, payload })
                // Show a loader during the brief grace period, then reload
                dispatch(loadingActions.startLoader(true))
                setTimeout(() => {
                    try {
                        dispatch({ type: SET_SWITCHING_PTP, switching: false })
                        navigateTo('/my-activities')
                        window.location.reload()
                    } catch (_) { }
                }, 1500)
            } catch (e) {
                // no-op; request.js will surface errors if needed
            }
        }
        const fail = () => dispatch({ type: SET_SWITCHING_PTP, switching: false })
        return dispatch(
            request({
                method: 'POST',
                url,
                success,
                fail,
                suppressLogoutOnAuthError: true,
                skipNotyFail: false,
                headerOptions: {
                    'hpi-device-type': 'web',
                    'hpi-app-version': packageJson.version
                }
            })
        )
    }
}

// Hydrate selected participant from localStorage so selection survives reloads
const savedSelected = (() => {
    try {
        return JSON.parse(localStorage.getItem('selected_ptp')) || null
    } catch (_) {
        return null
    }
})()

// Hydrate participants from sessionStorage if available
const savedParticipants = (() => {
    try {
        return JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEYS.caregiverParticipants)) || []
    } catch (_) {
        return []
    }
})()

const initialState = { participants: savedParticipants, selected: savedSelected, switching: false }

export default function reducer(state, action = {}) {
    const currentState = state === undefined ? initialState : state
    switch (action.type) {
        case SET_CAREGIVER_PARTICIPANTS: {
            // Also update sessionStorage when participants are set
            try {
                sessionStorage.setItem(SESSION_STORAGE_KEYS.caregiverParticipants, JSON.stringify(action.participants))
            } catch (_) { }
            // Preserve existing selection; do not clear when multiple participants exist
            // Optional: if current selection is no longer in list, clear it
            let preservedSelected = currentState.selected
            if (
                preservedSelected &&
                action.participants &&
                !action.participants.some(p => String(p.id) === String(preservedSelected.id))
            ) {
                preservedSelected = null
                try { localStorage.removeItem('selected_ptp') } catch (_) { }
            }
            return { ...currentState, participants: action.participants, selected: preservedSelected }
        }
        case SET_SELECTED_PTP:
            try {
                if (action.ptp) {
                    localStorage.setItem('selected_ptp', JSON.stringify(action.ptp))
                } else {
                    localStorage.removeItem('selected_ptp')
                }
            } catch (_) { }
            return { ...currentState, selected: action.ptp }
        case SET_SWITCHING_PTP:
            return { ...currentState, switching: !!action.switching }
        case USER_LOGGED_OUT:
            try {
                localStorage.removeItem('selected_ptp')
                sessionStorage.removeItem(SESSION_STORAGE_KEYS.caregiverParticipants)
            } catch (_) { }
            return { participants: [], selected: null, switching: false }
        default:
            return currentState
    }
}

